'use client';

/**
 * AuthGuard - Client-Side Authentication Gate
 *
 * Features:
 * - Wraps content that requires authentication
 * - Shows loading state while checking auth
 * - Redirects to login if not authenticated
 * - Supports different auth requirements (required, staff, admin, citizen)
 * - Shows access denied for role mismatches
 */

import React, { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Loader2, Lock, AlertCircle } from 'lucide-react';
import { useAppShell, useIsAuthenticated, useIsStaff, useIsAdmin, useIsCitizen } from '../AppShellContext';
import type { AuthRequirement } from '@/lib/routes/types';
import { AUTH_REDIRECTS, ROLE_DEFAULT_ROUTES } from '@/lib/routes/config';

// ============================================================================
// Types
// ============================================================================

interface AuthGuardProps {
  /** Authentication requirement level */
  requirement: AuthRequirement;

  /** Content to render when authorized */
  children: React.ReactNode;

  /** Custom loading component */
  loadingComponent?: React.ReactNode;

  /** Custom access denied component */
  accessDeniedComponent?: React.ReactNode;

  /** Redirect URL when not authenticated (overrides default) */
  redirectTo?: string;
}

// ============================================================================
// Component
// ============================================================================

export function AuthGuard({
  requirement,
  children,
  loadingComponent,
  accessDeniedComponent,
  redirectTo,
}: AuthGuardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isAuthLoading: isLoading, accessControlState } = useAppShell();
  const isAuthenticated = useIsAuthenticated();
  const isStaff = useIsStaff();
  const isAdmin = useIsAdmin();
  const isCitizen = useIsCitizen();

  // Wait for BOTH auth AND access control state to finish loading.
  // isAuthLoading tracks Supabase session resolution.
  // accessControlState.loading tracks the /api/access-control/context fetch.
  // Without waiting for both, role checks (isStaff/isAdmin) see null userContext
  // and incorrectly deny access.
  const isAccessControlLoading = accessControlState?.loading ?? true;

  const [checkComplete, setCheckComplete] = useState(false);
  const [accessStatus, setAccessStatus] = useState<'loading' | 'granted' | 'denied' | 'redirect'>('loading');

  useEffect(() => {
    if (isLoading || isAccessControlLoading) {
      return;
    }

    // Check access based on requirement
    const checkAccess = () => {
      switch (requirement) {
        case 'none':
          return 'granted';

        case 'optional':
          return 'granted';

        case 'required':
          if (!isAuthenticated) {
            return 'redirect';
          }
          return 'granted';

        case 'staff':
          if (!isAuthenticated) {
            return 'redirect';
          }
          if (!isStaff) {
            return 'denied';
          }
          return 'granted';

        case 'admin':
          if (!isAuthenticated) {
            return 'redirect';
          }
          if (!isAdmin) {
            return 'denied';
          }
          return 'granted';

        case 'platform':
          if (!isAuthenticated) {
            return 'redirect';
          }
          const platformRole = user?.platformRole || user?.app_metadata?.platform_role;
          if (!platformRole) {
            return 'denied';
          }
          return 'granted';

        case 'citizen':
          if (!isAuthenticated) {
            return 'redirect';
          }
          if (!isCitizen) {
            return 'denied';
          }
          return 'granted';

        default:
          return 'denied';
      }
    };

    const status = checkAccess();
    setAccessStatus(status);
    setCheckComplete(true);

    if (status === 'redirect') {
      // Determine redirect URL
      const targetUrl = redirectTo || AUTH_REDIRECTS.unauthenticated;
      const returnUrl = encodeURIComponent(pathname);
      router.push(`${targetUrl}?returnTo=${returnUrl}`);
    }
  }, [isLoading, isAccessControlLoading, isAuthenticated, isStaff, isAdmin, isCitizen, requirement, user, pathname, router, redirectTo]);

  // Public and optional routes do not depend on a role read.
  if (requirement === 'none' || requirement === 'optional') {
    return <>{children}</>;
  }

  // An earlier grant must not render while its authority is being rechecked.
  if (isLoading || isAccessControlLoading || !checkComplete || accessStatus === 'loading') {
    return loadingComponent || <AuthLoadingState />;
  }

  // Handle redirect (show loading while redirecting)
  if (accessStatus === 'redirect') {
    return loadingComponent || <AuthLoadingState message="Redirecting to login..." />;
  }

  // Handle access denied
  if (accessStatus === 'denied') {
    return accessDeniedComponent || <AccessDeniedState requirement={requirement} />;
  }

  // Access granted
  return <>{children}</>;
}

// ============================================================================
// Loading State
// ============================================================================

interface AuthLoadingStateProps {
  message?: string;
}

function AuthLoadingState({ message = 'Checking authentication...' }: AuthLoadingStateProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      <p className="text-sm text-slate-400">{message}</p>
    </div>
  );
}

// ============================================================================
// Access Denied State
// ============================================================================

interface AccessDeniedStateProps {
  requirement: AuthRequirement;
}

function AccessDeniedState({ requirement }: AccessDeniedStateProps) {
  const router = useRouter();
  const { user } = useAppShell();

  // Determine appropriate redirect based on user's role
  const getHomeRoute = () => {
    const role = user?.role;
    if (role && ROLE_DEFAULT_ROUTES[role as keyof typeof ROLE_DEFAULT_ROUTES]) {
      return ROLE_DEFAULT_ROUTES[role as keyof typeof ROLE_DEFAULT_ROUTES];
    }
    return '/dashboard';
  };

  const getMessage = () => {
    switch (requirement) {
      case 'staff':
        return 'This page is only accessible to officers, supervisors, and administrators.';
      case 'admin':
        return 'This page is only accessible to administrators.';
      case 'platform':
        return 'This page is only accessible to platform administrators.';
      case 'citizen':
        return 'This page is only accessible to citizen portal users.';
      default:
        return 'You do not have permission to access this page.';
    }
  };

  return (
    <div className="relative z-10 flex flex-col items-center justify-center min-h-[400px] gap-6 px-4">
      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/20">
        <Lock className="w-8 h-8 text-blue-400" />
      </div>

      <div className="text-center max-w-md">
        <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
        <p className="text-sm text-slate-400">{getMessage()}</p>
      </div>

      <div className="relative z-20 flex items-center gap-3">
        <button
          type="button"
          onClick={() => router.back()}
          className="px-4 py-2 text-sm text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
        >
          Go Back
        </button>
        <button
          type="button"
          onClick={() => router.push(getHomeRoute())}
          className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors cursor-pointer"
        >
          Go to Dashboard
        </button>
      </div>

      {/* Debug info in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-3 bg-slate-800/50 rounded-lg text-xs text-slate-500">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-3 h-3" />
            <span className="font-medium">Debug Info</span>
          </div>
          <pre className="mt-1">
            Requirement: {requirement}
            {'\n'}User Role: {user?.user_metadata?.role || 'none'}
            {'\n'}Platform Role: {user?.app_metadata?.platform_role || 'none'}
          </pre>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Export
// ============================================================================

export default AuthGuard;
